import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHome, FaTrophy, FaHistory, FaCoins, FaHandshake, FaBars, FaTimes, FaUsers, FaUserCircle, FaSignOutAlt, FaExclamationTriangle, FaUserShield, FaTwitch, FaFutbol } from 'react-icons/fa';
import { FaComputer } from 'react-icons/fa6';
import { useAuth } from '../../context/AuthContext';
import { useTwitchStatus } from '../../context/TwitchStatusContext';
import { PENALTIS_ENABLED } from '../../config/features';
import avatar from '../../views/assets/jizon2.png';

import './SideMenu.css';

const TWITCH_URL = 'https://twitch.tv/jizonn';

const NAV_GROUPS = [
  {
    label: 'Geral',
    items: [
      { page: 'home', path: '/', label: 'Início', Icon: FaHome },
      { page: 'parcerias', path: '/parcerias', label: 'Parcerias', Icon: FaHandshake },
      { page: 'comunidade', path: '/comunidade', label: 'Comunidade', Icon: FaUsers },
      ...(PENALTIS_ENABLED ? [{ page: 'penaltis', path: '/penaltis', label: 'Penáltis', Icon: FaFutbol, badge: 'Novo' }] : []),
    ],
  },
  {
    label: 'Apostas',
    items: [
      { page: 'bets', path: '/bets', label: 'Bets', Icon: FaCoins },
      { page: 'desafio', path: '/desafio', label: 'Desafio', Icon: FaTrophy },
      { page: 'historico', path: '/historico', label: 'Histórico', Icon: FaHistory },
    ],
  },
  {
    label: 'Mais',
    items: [
      { page: 'setup', path: '/setup', label: 'Setup', Icon: FaComputer },
    ],
  },
];

function SideMenu({ onNavigate, currentPage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const logoutRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { isLive, viewerCount, category } = useTwitchStatus();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (logoutRef.current && !logoutRef.current.contains(event.target)) {
        setShowLogoutConfirm(false);
      }
    };

    if (showLogoutConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogoutConfirm]);

  // Lock page scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavigation = (page) => {
    onNavigate?.(page);
    setIsOpen(false);
  };

  const handleLogoutConfirm = async () => {
    await logout();
    setShowLogoutConfirm(false);
    setIsOpen(false);
  };

  const displayName = user?.twitchProfile?.displayName || user?.username;
  const avatarUrl = user?.twitchProfile?.avatarUrl;

  const brand = (
    <a href="/" className="site-nav-brand" onClick={(e) => { e.preventDefault(); handleNavigation('home'); }}>
      <span className={`site-nav-brand-avatar ${isLive ? 'is-live' : ''}`}>
        <img src={avatar} alt="" width="34" height="34" />
      </span>
      <span className="site-nav-brand-text">
        <span className="site-nav-brand-name">jizoNN</span>
        <span className={`site-nav-brand-status ${isLive ? 'is-live' : ''}`}>
          {isLive ? 'Em direto' : 'Streamer'}
        </span>
      </span>
    </a>
  );

  return (
    <>
      <header className="mobile-topbar">
        {brand}
        <button className="menu-toggle" onClick={() => setIsOpen(v => !v)} aria-label="Menu" aria-expanded={isOpen}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      </header>

      <div className={`menu-overlay ${isOpen ? 'visible' : ''}`} onClick={() => setIsOpen(false)} />

      <nav className={`site-nav ${isOpen ? 'open' : ''}`} aria-label="Navegação principal">
        <div className="site-nav-header">{brand}</div>

        <div className="site-nav-groups">
          {NAV_GROUPS.map(group => (
            <div className="site-nav-group" key={group.label}>
              <span className="site-nav-group-label">{group.label}</span>
              <ul className="site-nav-list">
                {group.items.map(({ page, path, label, Icon, badge }) => (
                  <li key={page}>
                    <a
                      href={path}
                      onClick={(e) => { e.preventDefault(); handleNavigation(page); }}
                      className={`site-nav-link ${currentPage === page ? 'active' : ''}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      <Icon className="site-nav-link-icon" />
                      <span className="site-nav-link-label">{label}</span>
                      {badge && <span className="site-nav-badge">{badge}</span>}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="site-nav-footer">
          <a
            href={TWITCH_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`stream-card ${isLive ? 'is-live' : ''}`}
          >
            <span className="stream-card-head">
              <span className="stream-card-dot" />
              <span className="stream-card-state">{isLive ? 'Em direto agora' : 'Offline'}</span>
              <FaTwitch className="stream-card-twitch" />
            </span>
            <span className="stream-card-text">
              {isLive
                ? [category, viewerCount != null ? `${viewerCount} a ver` : null].filter(Boolean).join(' · ') || 'Assiste na Twitch'
                : 'Segue na Twitch para não perderes a próxima live.'}
            </span>
            <span className="stream-card-cta">{isLive ? 'Assistir' : 'Seguir na Twitch'}</span>
          </a>

          {user?.isStaff && (
            <a
              href="/backoffice"
              className="site-nav-link"
              onClick={(e) => { e.preventDefault(); navigate('/backoffice'); setIsOpen(false); }}
            >
              <FaUserShield className="site-nav-link-icon" />
              <span className="site-nav-link-label">Backoffice</span>
            </a>
          )}

          {isAuthenticated && (
            <div className="user-session" ref={logoutRef}>
              <a href="/comunidade" onClick={(e) => { e.preventDefault(); handleNavigation('comunidade'); }} className="user-session-link">
                {avatarUrl ? (
                  <img className="user-session-avatar" src={avatarUrl} alt="" />
                ) : (
                  <FaUserCircle className="user-session-avatar" />
                )}
                <span className="user-session-name">{displayName}</span>
              </a>
              <button
                className="user-session-logout"
                onClick={() => setShowLogoutConfirm(v => !v)}
                aria-label="Sair"
                title="Sair"
              >
                <FaSignOutAlt />
              </button>
              {showLogoutConfirm && (
                <div className="logout-confirm">
                  <div className="logout-confirm-header">
                    <FaExclamationTriangle className="logout-confirm-icon" />
                    <span>Sair da conta <strong>{displayName}</strong>?</span>
                  </div>
                  <div className="logout-confirm-actions">
                    <button className="btn" onClick={() => setShowLogoutConfirm(false)}>Cancelar</button>
                    <button className="btn btn-danger" onClick={handleLogoutConfirm}>Sair</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

export default SideMenu;
